import { t } from "@lingui/macro";
import {
  ArrowClockwise,
  ArrowCounterClockwise,
  CaretDown,
  CaretUp,
  CircleNotch,
  ClockClockwise,
  CubeFocus,
  FilePdf,
  Hash,
  LineSegment,
  LinkSimple,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  Notepad,
  Sparkle,
} from "@phosphor-icons/react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RichInput,
  Separator,
  Toggle,
  Tooltip,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { usePrintResume } from "@/client/services/resume";
import { useAiStore } from "@/client/stores/ai";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore, useTemporalResumeStore } from "@/client/stores/resume";

export const BuilderToolbar = () => {
  const { toast } = useToast();
  const setValue = useResumeStore((state) => state.setValue);
  const undo = useTemporalResumeStore((state) => state.undo);
  const redo = useTemporalResumeStore((state) => state.redo);
  const frameRef = useBuilderStore((state) => state.frame.ref);
  const [collapsed, setCollapsed] = useState(false);

  const id = useResumeStore((state) => state.resume.id);
  const notes = useResumeStore((state) => state.resume.data.metadata.notes);
  const isPublic = useResumeStore((state) => state.resume.visibility === "public");
  const pageOptions = useResumeStore((state) => state.resume.data.metadata.page.options);

  const toggleAiPanel = useAiStore((state) => state.togglePanel);
  const aiPanelOpen = useAiStore((state) => state.panelOpen);

  const { printResume, loading } = usePrintResume();

  const onPrint = async () => {
    const { url } = await printResume({ id });

    const openInNewTab = (url: string) => {
      const win = window.open(url, "_blank");
      if (win) win.focus();
    };

    openInNewTab(url);
  };

  const onCopy = async () => {
    const { url } = await printResume({ id });
    await navigator.clipboard.writeText(url);

    toast({
      variant: "success",
      title: t`A link has been copied to your clipboard.`,
      description: t`Anyone with this link can view and download the resume. Share it on your profile or with recruiters.`,
    });
  };

  const onZoomIn = () => frameRef?.contentWindow?.postMessage({ type: "ZOOM_IN" }, "*");
  const onZoomOut = () => frameRef?.contentWindow?.postMessage({ type: "ZOOM_OUT" }, "*");
  const onResetView = () => frameRef?.contentWindow?.postMessage({ type: "RESET_VIEW" }, "*");
  const onCenterView = () => frameRef?.contentWindow?.postMessage({ type: "CENTER_VIEW" }, "*");

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 mx-auto hidden text-center md:block" data-testid="builder-toolbar">
      <AnimatePresence mode="wait">
        {collapsed ? (
          <motion.div
            key="collapsed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.15 }}
            className="pb-3"
          >
            <Tooltip content={t`Show Toolbar`}>
              <button
                onClick={() => setCollapsed(false)}
                className="inline-flex items-center gap-x-1 rounded-full bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow-lg backdrop-blur-sm transition-colors hover:bg-background hover:text-foreground"
              >
                <CaretUp size={12} weight="bold" />
                <span>{t`Toolbar`}</span>
              </button>
            </Tooltip>
          </motion.div>
        ) : (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.2 }}
            className="pb-6"
          >
            <div className="inline-flex items-center justify-center rounded-full bg-background px-4 shadow-xl">
              <Tooltip content={t`Undo`}>
                <Button size="icon" variant="ghost" className="rounded-none" onClick={() => undo()}>
                  <ArrowCounterClockwise />
                </Button>
              </Tooltip>

              <Tooltip content={t`Redo`}>
                <Button size="icon" variant="ghost" className="rounded-none" onClick={() => redo()}>
                  <ArrowClockwise />
                </Button>
              </Tooltip>

              <Separator orientation="vertical" className="h-9" />

              <Tooltip content={t`Zoom In`}>
                <Button size="icon" variant="ghost" className="rounded-none" onClick={onZoomIn}>
                  <MagnifyingGlassPlus />
                </Button>
              </Tooltip>

              <Tooltip content={t`Zoom Out`}>
                <Button size="icon" variant="ghost" className="rounded-none" onClick={onZoomOut}>
                  <MagnifyingGlassMinus />
                </Button>
              </Tooltip>

              <Tooltip content={t`Reset Zoom`}>
                <Button size="icon" variant="ghost" className="rounded-none" onClick={onResetView}>
                  <ClockClockwise />
                </Button>
              </Tooltip>

              <Tooltip content={t`Center Artboard`}>
                <Button size="icon" variant="ghost" className="rounded-none" onClick={onCenterView}>
                  <CubeFocus />
                </Button>
              </Tooltip>

              <Separator orientation="vertical" className="h-9" />

              <Tooltip content={t`Toggle Page Break Line`}>
                <Toggle
                  className="rounded-none"
                  pressed={pageOptions.breakLine}
                  onPressedChange={(pressed) => {
                    setValue("metadata.page.options.breakLine", pressed);
                  }}
                >
                  <LineSegment />
                </Toggle>
              </Tooltip>

              <Tooltip content={t`Toggle Page Numbers`}>
                <Toggle
                  className="rounded-none"
                  pressed={pageOptions.pageNumbers}
                  onPressedChange={(pressed) => {
                    setValue("metadata.page.options.pageNumbers", pressed);
                  }}
                >
                  <Hash />
                </Toggle>
              </Tooltip>

              <Separator orientation="vertical" className="h-9" />

              <Popover>
                <Tooltip content={t`Notes`}>
                  <PopoverTrigger asChild>
                    <Button size="icon" variant="ghost" className="rounded-none">
                      <Notepad />
                    </Button>
                  </PopoverTrigger>
                </Tooltip>
                <PopoverContent side="top" align="center" className="w-80">
                  <div className="space-y-2">
                    <p className="text-sm font-semibold">{t`Notes`}</p>
                    <p className="text-xs text-muted-foreground">
                      {t`Private notes for this resume. Not visible to anyone else.`}
                    </p>
                    <RichInput
                      content={notes}
                      hideToolbar
                      onChange={(content) => setValue("metadata.notes", content)}
                    />
                  </div>
                </PopoverContent>
              </Popover>

              <Tooltip content={t`Copy Link to Resume`}>
                <Button
                  size="icon"
                  variant="ghost"
                  className="rounded-none"
                  onClick={onCopy}
                  disabled={!isPublic}
                >
                  <LinkSimple />
                </Button>
              </Tooltip>

              <Tooltip content={t`Download PDF`}>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={onPrint}
                  disabled={loading}
                  className="rounded-none"
                >
                  {loading ? <CircleNotch className="animate-spin" /> : <FilePdf />}
                </Button>
              </Tooltip>

              <Separator orientation="vertical" className="h-9" />

              <Tooltip content={aiPanelOpen ? t`Close AI Assistant` : t`Open AI Assistant`}>
                <Button
                  size="icon"
                  variant="ghost"
                  className={cn(
                    "rounded-none",
                    aiPanelOpen ? "ai-btn" : "ai-toolbar-idle",
                  )}
                  onClick={toggleAiPanel}
                >
                  <Sparkle weight={aiPanelOpen ? "fill" : "duotone"} />
                </Button>
              </Tooltip>

              <Separator orientation="vertical" className="h-9" />

              <Tooltip content={t`Hide Toolbar`}>
                <Button
                  size="icon"
                  variant="ghost"
                  className="rounded-none"
                  onClick={() => setCollapsed(true)}
                >
                  <CaretDown size={14} />
                </Button>
              </Tooltip>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
